import { createTheme, alpha } from '@mui/material/styles';

// SafeLine Pickup - Modern Orange Light Theme Color Scheme
const safeLineColors = {
  // Primary Orange Palette
  primary: {
    flame: '#ff6b35',      // Main brand color, buttons, CTAs
    bright: '#ff9500',     // Hover states, secondary actions
    deep: '#e55100',       // Active states, pressed buttons
    tint: '#fff4f0',       // Light backgrounds, subtle highlights
  },
  
  // Light Theme Base
  backgrounds: {
    pure: '#ffffff',       // Main app backgrounds
    light: '#f8f9fa',      // Card backgrounds, panels
    cool: '#e9ecef',       // Borders, dividers
    medium: '#dee2e6',     // Input fields, secondary surfaces
  },
  
  // Success States (Pickup Completed)
  success: {
    emerald: '#10b981',    // Success messages, completed pickups
    light: '#d1fae5',      // Success background highlights
    forest: '#047857',     // Success button hover
  },
  
  // Warning States (Attention Needed)
  warning: {
    amber: '#f59e0b',      // Pending pickups, processing
    light: '#fef3c7',      // Warning background highlights
    dark: '#d97706',       // Warning button hover
  },
  
  // Error States (Issues/Alerts)
  error: {
    red: '#ef4444',        // Error messages, failed scans
    light: '#fee2e2',      // Error background highlights
    dark: '#dc2626',       // Error button hover
  },
  
  // Text Colors
  text: {
    darkCharcoal: '#1f2937',    // Primary text on light backgrounds
    mediumCharcoal: '#374151',  // Secondary text, labels
    lightCharcoal: '#6b7280',   // Placeholder text, disabled states
    mutedGray: '#9ca3af',       // Timestamps, captions
  }
};

// Theme utility functions
const themeUtils = {
  // Create alpha variations of colors
  alpha: (color, opacity) => alpha(color, opacity),
  
  // Generate gradients
  gradient: (color1, color2, direction = '135deg') => 
    `linear-gradient(${direction}, ${color1} 0%, ${color2} 100%)`,
  
  // Generate shadows with consistent opacity
  shadow: (color = '#000000', blur = 4, spread = 0, opacity = 0.1) =>
    `0 ${spread}px ${blur}px ${alpha(color, opacity)}`,
  
  // Generate hover states
  hover: (baseColor, amount = 0.08) => alpha(baseColor, amount),
  
  // Generate border colors
  border: (color, opacity = 0.12) => alpha(color, opacity),
};

// Extended theme configuration
const themeConfig = {
  palette: {
    primary: {
      main: safeLineColors.primary.flame,
      light: safeLineColors.primary.bright,
      dark: safeLineColors.primary.deep,
      contrastText: safeLineColors.backgrounds.pure,
    },
    secondary: {
      main: safeLineColors.text.mediumCharcoal,
      light: safeLineColors.text.lightCharcoal,
      dark: safeLineColors.text.darkCharcoal,
      contrastText: safeLineColors.backgrounds.pure,
    },
    background: {
      default: safeLineColors.backgrounds.pure,
      paper: safeLineColors.backgrounds.light,
    },
    success: {
      main: safeLineColors.success.emerald,
      light: safeLineColors.success.light,
      dark: safeLineColors.success.forest,
      contrastText: safeLineColors.backgrounds.pure,
    },
    warning: {
      main: safeLineColors.warning.amber,
      light: safeLineColors.warning.light,
      dark: safeLineColors.warning.dark,
      contrastText: safeLineColors.backgrounds.pure,
    },
    error: {
      main: safeLineColors.error.red,
      light: safeLineColors.error.light,
      dark: safeLineColors.error.dark,
      contrastText: safeLineColors.backgrounds.pure,
    },
    info: {
      main: safeLineColors.primary.bright,
      light: safeLineColors.primary.tint,
      dark: safeLineColors.primary.deep,
      contrastText: safeLineColors.backgrounds.pure,
    },
    text: {
      primary: safeLineColors.text.darkCharcoal,
      secondary: safeLineColors.text.mediumCharcoal,
      disabled: safeLineColors.text.lightCharcoal,
      hint: safeLineColors.text.mutedGray,
    },
    divider: safeLineColors.backgrounds.cool,
  },
  
  // Custom color extensions
  colors: safeLineColors,
  
  // Custom shadows
  shadows: {
    subtle: themeUtils.shadow('#000000', 2, 0, 0.05),
    card: themeUtils.shadow('#000000', 4, 0, 0.1),
    elevated: themeUtils.shadow('#000000', 8, 0, 0.15),
    floating: themeUtils.shadow('#000000', 12, 0, 0.2),
    primary: themeUtils.shadow(safeLineColors.primary.flame, 8, 0, 0.3),
    success: themeUtils.shadow(safeLineColors.success.emerald, 8, 0, 0.3),
    warning: themeUtils.shadow(safeLineColors.warning.amber, 8, 0, 0.3),
    error: themeUtils.shadow(safeLineColors.error.red, 8, 0, 0.3),
  },
  
  // Custom borders
  borders: {
    subtle: `1px solid ${safeLineColors.backgrounds.cool}`,
    medium: `1px solid ${safeLineColors.backgrounds.medium}`,
    primary: `1px solid ${safeLineColors.primary.flame}`,
    success: `1px solid ${safeLineColors.success.emerald}`,
    warning: `1px solid ${safeLineColors.warning.amber}`,
    error: `1px solid ${safeLineColors.error.red}`,
  },
  
  // Custom gradients
  gradients: {
    primary: themeUtils.gradient(safeLineColors.primary.flame, safeLineColors.primary.deep),
    primaryHover: themeUtils.gradient(safeLineColors.primary.deep, safeLineColors.error.dark),
    success: themeUtils.gradient(safeLineColors.success.emerald, safeLineColors.success.forest),
    successHover: themeUtils.gradient(safeLineColors.success.forest, safeLineColors.success.forest),
    background: themeUtils.gradient(safeLineColors.backgrounds.pure, safeLineColors.backgrounds.light, '180deg'),
    sidebar: themeUtils.gradient(safeLineColors.primary.flame, safeLineColors.primary.deep, '180deg'),
  },
  
  shape: {
    borderRadius: 8,
  },
  
  typography: {
    fontFamily: [
      'Inter',
      'Roboto',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
      letterSpacing: '-0.025em',
      color: safeLineColors.text.darkCharcoal,
    },
    h2: {
      fontWeight: 700,
      fontSize: '2rem',
      letterSpacing: '-0.025em',
      color: safeLineColors.text.darkCharcoal,
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.75rem',
      letterSpacing: '-0.025em',
      color: safeLineColors.text.darkCharcoal,
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
      color: safeLineColors.text.darkCharcoal,
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
      color: safeLineColors.text.darkCharcoal,
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
      color: safeLineColors.text.darkCharcoal,
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 500,
      color: safeLineColors.text.mediumCharcoal,
    },
    subtitle2: {
      fontSize: '0.875rem',
      fontWeight: 500,
      color: safeLineColors.text.mediumCharcoal,
    },
    body1: {
      fontSize: '1rem',
      color: safeLineColors.text.darkCharcoal,
    },
    body2: {
      fontSize: '0.875rem',
      color: safeLineColors.text.mediumCharcoal,
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
    },
  },
};

// Create the theme instance
const theme = createTheme({
  ...themeConfig,
  shadows: [
    'none',
    themeConfig.shadows.subtle,
    themeConfig.shadows.card,
    themeConfig.shadows.elevated,
    themeConfig.shadows.floating,
    themeConfig.shadows.primary,
    themeConfig.shadows.success,
    themeConfig.shadows.warning,
    themeConfig.shadows.error,
    themeConfig.shadows.floating,
    themeConfig.shadows.floating,
    themeConfig.shadows.floating,
    themeConfig.shadows.floating,
    themeConfig.shadows.floating,
    themeConfig.shadows.floating,
    themeConfig.shadows.floating,
    themeConfig.shadows.floating,
    themeConfig.shadows.floating,
    themeConfig.shadows.floating,
    themeConfig.shadows.floating,
    themeConfig.shadows.floating,
    themeConfig.shadows.floating,
    themeConfig.shadows.floating,
    themeConfig.shadows.floating,
    themeConfig.shadows.floating,
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 8,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          },
        },
        containedPrimary: {
          background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
          color: '#ffffff',
          '&:hover': {
            background: 'linear-gradient(135deg, #ea580c 0%, #dc2626 100%)',
            color: '#ffffff',
          },
        },
        containedSecondary: {
          background: 'linear-gradient(135deg, #64748b 0%, #475569 100%)',
          color: '#ffffff',
          '&:hover': {
            background: 'linear-gradient(135deg, #475569 0%, #334155 100%)',
            color: '#ffffff',
          },
        },
        outlined: {
          borderWidth: 1.5,
          '&.MuiButton-outlinedPrimary': {
            borderColor: '#f97316',
            color: '#f97316',
            '&:hover': {
              backgroundColor: 'rgba(249, 115, 22, 0.04)',
            },
          },
          '&.MuiButton-outlinedSecondary': {
            borderColor: '#64748b',
            color: '#64748b',
            '&:hover': {
              backgroundColor: 'rgba(100, 116, 139, 0.04)',
            },
          },
        },
        containedSuccess: {
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          color: '#ffffff',
          '&:hover': {
            background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
            color: '#ffffff',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          border: themeConfig.borders.subtle,
          boxShadow: themeConfig.shadows.subtle,
          transition: 'all 0.2s ease',
        },
        elevation1: {
          boxShadow: themeConfig.shadows.card,
        },
        elevation2: {
          boxShadow: themeConfig.shadows.elevated,
        },
        elevation3: {
          boxShadow: themeConfig.shadows.floating,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            backgroundColor: safeLineColors.backgrounds.pure,
            transition: 'all 0.2s ease',
            '& fieldset': {
              borderColor: safeLineColors.backgrounds.cool,
            },
            '&:hover fieldset': {
              borderColor: safeLineColors.backgrounds.medium,
            },
            '&.Mui-focused fieldset': {
              borderColor: safeLineColors.primary.flame,
              boxShadow: `0 0 0 2px ${alpha(safeLineColors.primary.flame, 0.1)}`,
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 500,
          backgroundColor: 'transparent',
          border: 'none',
        },
        colorPrimary: {
          backgroundColor: '#fb923c',
          color: '#000000',
        },
        colorSecondary: {
          backgroundColor: '#94a3b8',
          color: '#475569',
        },
        colorSuccess: {
          backgroundColor: '#34d399',
          color: '#059669',
        },
        colorWarning: {
          backgroundColor: '#fbbf24',
          color: '#d97706',
        },
        colorError: {
          backgroundColor: '#f87171',
          color: '#dc2626',
        },
        colorInfo: {
          backgroundColor: '#38bdf8',
          color: '#0284c7',
        },
        outlined: {
          borderWidth: 1.5,
          backgroundColor: 'transparent',
        },
        outlinedPrimary: {
          borderColor: '#f97316',
          color: '#f97316',
          backgroundColor: 'transparent',
        },
        outlinedSecondary: {
          borderColor: '#64748b',
          color: '#64748b',
          backgroundColor: 'transparent',
        },
        outlinedSuccess: {
          borderColor: '#10b981',
          color: '#10b981',
          backgroundColor: 'transparent',
        },
        outlinedWarning: {
          borderColor: '#f59e0b',
          color: '#f59e0b',
          backgroundColor: 'transparent',
        },
        outlinedError: {
          borderColor: '#ef4444',
          color: '#ef4444',
          backgroundColor: 'transparent',
        },
        outlinedInfo: {
          borderColor: '#0ea5e9',
          color: '#0ea5e9',
          backgroundColor: 'transparent',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: '#f97316',
          height: 3,
          borderRadius: '3px 3px 0 0',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          '&.Mui-selected': {
            color: '#f97316',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 600,
          backgroundColor: '#fafaf9',
        },
        body: {
          borderBottom: themeConfig.borders.subtle,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: themeConfig.shadows.card,
          backgroundColor: safeLineColors.backgrounds.pure,
          color: safeLineColors.text.darkCharcoal,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: 'none',
          boxShadow: themeConfig.shadows.elevated,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: themeConfig.shadows.card,
          border: themeConfig.borders.subtle,
          '&:hover': {
            boxShadow: themeConfig.shadows.elevated,
            transform: 'translateY(-2px)',
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        standardSuccess: {
          backgroundColor: safeLineColors.success.light,
          color: safeLineColors.success.forest,
        },
        standardWarning: {
          backgroundColor: safeLineColors.warning.light,
          color: safeLineColors.warning.dark,
        },
        standardError: {
          backgroundColor: safeLineColors.error.light,
          color: safeLineColors.error.dark,
        },
        standardInfo: {
          backgroundColor: safeLineColors.primary.tint,
          color: safeLineColors.primary.deep,
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        root: {
          '& .MuiTypography-root': {
            fontWeight: 700,
            color: '#000000 !important', // Force black text
          },
          '& .MuiListItemText-primary': {
            color: '#000000 !important',
            fontWeight: 700,
          },
          '& .MuiListItemText-secondary': {
            color: '#374151 !important',
          },
        },
        primary: {
          color: '#000000 !important',
          fontWeight: 700,
        },
        secondary: {
          color: '#374151 !important',
        },
      },
    },
    // Add global style override for ListItemText
    MuiCssBaseline: {
      styleOverrides: {
        '.MuiListItemText-root .MuiTypography-root': {
          color: '#000000 !important',
          fontWeight: 700,
        },
        '.MuiListItemText-primary': {
          color: '#000000 !important',
          fontWeight: 700,
        },
        '.MuiListItemText-secondary': {
          color: '#374151 !important',
        },
      },
    },
  },
});

// Add custom properties to theme
theme.colors = safeLineColors;
theme.shadows = themeConfig.shadows;
theme.borders = themeConfig.borders;
theme.gradients = themeConfig.gradients;
theme.utils = themeUtils;

export default theme;
